/**
 * Late-fusion document-type model — the inference half, shared verbatim between
 * the offline trainer (`tools/doc-eval/train.ts`) and the browser. Keeping the
 * predict math here means training and runtime can never disagree.
 *
 * A {@link FusionModel} is the JSON emitted by the trainer: four experts (text
 * naive-Bayes, layout/colour nearest-centroid, structure standardised-centroid)
 * plus the fusion weights. {@link classifyFeatures} turns one document's
 * {@link DocFeatures} into a class probability vector and the per-expert votes.
 */
// Type-only import (elided at runtime) so this module has no runtime relative
// import — lets the Node trainer load it via type-stripping without a bundler.
import type { DocFeatures } from './document-features';

/**
 * The "structure" expert's input vector — form factor + markers, in a fixed
 * order. Lives here (not in document-features) so the trainer and the browser
 * share one definition and can never drift.
 */
export function structVector(f: DocFeatures): number[] {
  return [
    Math.max(-2, Math.min(2, Math.log2(f.aspect || 1))),
    f.fill,
    f.orientation === 'portrait' ? 1 : 0,
    f.orientation === 'landscape' ? 1 : 0,
    f.hasMrz ? 1 : 0,
    f.tableness,
    f.photoPresent ? 1 : 0,
    f.photoPresent ? f.photoX : 0,
    f.photoPresent ? f.photoY : 0,
  ];
}

export interface TextModel {
  prior: number[];
  logProb: number[][];
  defaultLog: number[];
  vocab: Record<string, number>;
}
/**
 * Multi-prototype experts: each class is represented by *several* prototype
 * vectors, not one centroid — so a class with very different sub-types (e.g. RG
 * issued by different states) is matched to whichever variant it resembles,
 * instead of an averaged-out blur. `protos[c]` is class c's list of prototypes.
 */
export interface CentModel {
  protos: number[][][];
}
export interface StructModel {
  mean: number[];
  std: number[];
  protos: number[][][];
}

export type Modality = 'text' | 'layout' | 'color' | 'struct';
export const MODALITIES: Modality[] = ['text', 'layout', 'color', 'struct'];

export interface FusionModel {
  labels: string[];
  weights: Record<Modality, number>;
  text: TextModel;
  layout: CentModel;
  color: CentModel;
  struct: StructModel;
}

export interface FusionResult {
  /** Best class key. */
  type: string;
  /** Fused probability of the winning class (0..1). */
  prob: number;
  /** Full fused probability vector, aligned to `model.labels`. */
  probs: number[];
  /** Each expert's own top vote, for transparency / debugging. */
  experts: { modality: Modality; type: string; prob: number }[];
}

// --- shared vector helpers ---------------------------------------------------
export function dot(a: number[], b: number[]): number {
  let s = 0;
  for (let i = 0; i < a.length; i++) s += a[i] * b[i];
  return s;
}
export function l2norm(v: number[]): number[] {
  const n = Math.sqrt(dot(v, v)) || 1;
  return v.map((x) => x / n);
}
export function softmax(v: number[]): number[] {
  const m = Math.max(...v);
  const ex = v.map((x) => Math.exp(x - m));
  const s = ex.reduce((a, b) => a + b, 0) || 1;
  return ex.map((x) => x / s);
}

// --- per-expert predictors (mirrored by the trainer's training side) ---------
export function predictText(m: TextModel, tokens: string[]): number[] {
  const scores = m.prior.map((p, c) => {
    let s = p;
    for (const tok of tokens) {
      const j = m.vocab[tok];
      s += j === undefined ? m.defaultLog[c] : m.logProb[c][j];
    }
    return s;
  });
  return softmax(scores);
}
export function predictCosine(m: CentModel, vec: number[]): number[] {
  // Score a class by its best-matching prototype (cosine ≈ dot on unit vectors).
  const scores = m.protos.map((ps) => (ps.length ? Math.max(...ps.map((p) => dot(p, vec))) : -1e9));
  return softmax(scores.map((s) => 8 * s));
}
export function predictStruct(m: StructModel, raw: number[]): number[] {
  const z = raw.map((x, d) => (x - m.mean[d]) / m.std[d]);
  const dist = m.protos.map((ps) =>
    ps.length
      ? Math.min(...ps.map((p) => Math.sqrt(p.reduce((s, pd, d) => s + (pd - z[d]) ** 2, 0))))
      : 1e9,
  );
  return softmax(dist.map((x) => -x));
}

function argmax(v: number[]): number {
  let bi = 0;
  for (let i = 1; i < v.length; i++) if (v[i] > v[bi]) bi = i;
  return bi;
}

/** Run all four experts on one document's features and fuse them. */
export function classifyFeatures(model: FusionModel, f: DocFeatures): FusionResult {
  const per: Record<Modality, number[]> = {
    text: predictText(model.text, f.tokens),
    layout: predictCosine(model.layout, l2norm(f.layout)),
    color: predictCosine(model.color, l2norm(f.color)),
    struct: predictStruct(model.struct, structVector(f)),
  };
  const C = model.labels.length;
  const probs = new Array(C).fill(0);
  for (const m of MODALITIES) {
    const w = model.weights[m] ?? 0;
    for (let c = 0; c < C; c++) probs[c] += w * per[m][c];
  }
  const wSum = MODALITIES.reduce((s, m) => s + (model.weights[m] ?? 0), 0) || 1;
  for (let c = 0; c < C; c++) probs[c] /= wSum;

  const top = argmax(probs);
  const experts = MODALITIES.map((m) => {
    const i = argmax(per[m]);
    return { modality: m, type: model.labels[i], prob: per[m][i] };
  });
  return { type: model.labels[top], prob: probs[top], probs, experts };
}
